import React from 'react';

export default function PrivacyPolicy() {
     return (
          <div className='min-h-screen bg-black text-white'>
               <div className='max-w-3xl mx-auto px-4 py-16'>
                    <h1 className='text-3xl font-bold mb-2'>Privacy Policy</h1>
                    <p className='text-white/40 text-sm mb-8'>Last updated: April 6, 2026</p>

                    <div className='space-y-8 text-white/80 text-sm leading-relaxed'>
                         <section>
                              <h2 className='text-white text-lg font-bold mb-3'>1. Introduction</h2>
                              <p>
                                   Ripout Rentals&reg; ("we," "us," or "our") operates ripoutrentals.com and related
                                   services. This Privacy Policy explains how we collect, use, disclose, and
                                   safeguard your information when you visit our website or use our services.
                              </p>
                         </section>

                         <section>
                              <h2 className='text-white text-lg font-bold mb-3'>2. Information We Collect</h2>
                              <p className='mb-3'>We may collect the following types of information:</p>
                              <ul className='list-disc pl-6 space-y-2'>
                                   <li>
                                        <strong className='text-white'>Personal Information:</strong> Name, email address,
                                        phone number, and project details you provide when submitting forms, requesting
                                        rentals, or making bookings.
                                   </li>
                                   <li>
                                        <strong className='text-white'>Payment Information:</strong> Credit card and billing
                                        details processed securely through our payment provider, Stripe. We do not store
                                        your full payment card details on our servers.
                                   </li>
                                   <li>
                                        <strong className='text-white'>Partial Form Data:</strong> If you begin filling out a
                                        form but do not complete it, we may collect the information you have entered up to
                                        that point. This allows us to follow up with you to assist with your inquiry or
                                        booking.
                                   </li>
                                   <li>
                                        <strong className='text-white'>Usage Data:</strong> We collect anonymous analytics
                                        data including pages visited, click interactions, scroll depth, browser type,
                                        operating system, device type, screen resolution, and approximate geographic
                                        location (city/state level) derived from your IP address.
                                   </li>
                                   <li>
                                        <strong className='text-white'>Advertising Data:</strong> If you arrive at our
                                        website through an advertisement, we may collect tracking identifiers such as Google
                                        Click ID (GCLID) and UTM parameters to measure advertising effectiveness and
                                        attribute conversions.
                                   </li>
                                   <li>
                                        <strong className='text-white'>Referrer Information:</strong> The website or source
                                        that referred you to our site.
                                   </li>
                              </ul>
                         </section>

                         <section>
                              <h2 className='text-white text-lg font-bold mb-3'>3. How We Use Your Information</h2>
                              <p className='mb-3'>We use the information we collect to:</p>
                              <ul className='list-disc pl-6 space-y-2'>
                                   <li>Process and manage your rental bookings and payments</li>
                                   <li>Send booking confirmations, invoices, proposals, and related communications</li>
                                   <li>Respond to your inquiries and provide customer support</li>
                                   <li>
                                        Follow up on incomplete form submissions to help you complete your booking or
                                        inquiry
                                   </li>
                                   <li>
                                        Improve our website, services, and user experience through anonymous analytics
                                   </li>
                                   <li>
                                        Measure the effectiveness of our advertising campaigns and optimize our marketing
                                        efforts
                                   </li>
                                   <li>Generate heatmaps and analytics to understand how visitors interact with our site</li>
                                   <li>Send you relevant communications about our services (you may opt out at any time)</li>
                                   <li>Comply with legal obligations</li>
                              </ul>
                         </section>

                         <section>
                              <h2 className='text-white text-lg font-bold mb-3'>4. Cookies and Tracking Technologies</h2>
                              <p>
                                   We use session storage and anonymous visitor identifiers to track site usage. We do
                                   not use traditional tracking cookies. Our analytics system generates an anonymous
                                   fingerprint based on non-identifying device characteristics (screen size, language,
                                   timezone) to estimate unique visitor counts. Your raw IP address is not stored — only
                                   an irreversible hash is used for analytics purposes, and approximate geolocation
                                   (city/state) is derived and stored separately.
                              </p>
                         </section>

                         <section>
                              <h2 className='text-white text-lg font-bold mb-3'>5. Third-Party Services</h2>
                              <p className='mb-3'>We use the following third-party services that may process your data:</p>
                              <ul className='list-disc pl-6 space-y-2'>
                                   <li>
                                        <strong className='text-white'>Stripe:</strong> For secure payment processing.
                                        Subject to{' '}
                                        <a
                                             href='https://stripe.com/privacy'
                                             target='_blank'
                                             rel='noopener noreferrer'
                                             className='text-yellow hover:underline'
                                        >
                                             Stripe's Privacy Policy
                                        </a>
                                        .
                                   </li>
                                   <li>
                                        <strong className='text-white'>Resend:</strong> For sending transactional emails
                                        (booking confirmations, invoices, proposals).
                                   </li>
                                   <li>
                                        <strong className='text-white'>Supabase:</strong> For secure data storage and
                                        application hosting.
                                   </li>
                                   <li>
                                        <strong className='text-white'>Vercel:</strong> For website hosting and delivery.
                                   </li>
                                   <li>
                                        <strong className='text-white'>Google Ads:</strong> For advertising measurement and
                                        conversion tracking.
                                   </li>
                              </ul>
                         </section>

                         <section>
                              <h2 className='text-white text-lg font-bold mb-3'>6. Data Retention</h2>
                              <p>
                                   We retain your personal information for as long as necessary to provide our services,
                                   fulfill the purposes described in this policy, and comply with legal obligations.
                                   Anonymous analytics data may be retained indefinitely. You may request deletion of your
                                   personal data at any time by contacting us.
                              </p>
                         </section>

                         <section>
                              <h2 className='text-white text-lg font-bold mb-3'>7. Data Security</h2>
                              <p>
                                   We implement reasonable technical and organizational measures to protect your
                                   information. All data is transmitted over encrypted connections (HTTPS). Payment
                                   information is handled exclusively by Stripe and never touches our servers. However,
                                   no method of electronic transmission or storage is 100% secure.
                              </p>
                         </section>

                         <section>
                              <h2 className='text-white text-lg font-bold mb-3'>8. Your Rights</h2>
                              <p className='mb-3'>You have the right to:</p>
                              <ul className='list-disc pl-6 space-y-2'>
                                   <li>Request access to the personal data we hold about you</li>
                                   <li>Request correction of inaccurate personal data</li>
                                   <li>Request deletion of your personal data</li>
                                   <li>Opt out of marketing communications at any time</li>
                                   <li>Request information about how your data is used</li>
                              </ul>
                              <p className='mt-3'>
                                   To exercise any of these rights, please contact us at{' '}
                                   <a href='mailto:sales@ripoutrentals.com' className='text-yellow hover:underline'>
                                        sales@ripoutrentals.com
                                   </a>
                                   .
                              </p>
                         </section>

                         <section>
                              <h2 className='text-white text-lg font-bold mb-3'>9. Children's Privacy</h2>
                              <p>
                                   Our services are not directed to individuals under the age of 18. We do not knowingly
                                   collect personal information from children.
                              </p>
                         </section>

                         <section>
                              <h2 className='text-white text-lg font-bold mb-3'>10. Changes to This Policy</h2>
                              <p>
                                   We may update this Privacy Policy from time to time. Changes will be posted on this
                                   page with an updated "Last updated" date. Your continued use of our website after
                                   changes constitutes acceptance of the updated policy.
                              </p>
                         </section>

                         <section>
                              <h2 className='text-white text-lg font-bold mb-3'>11. Contact Us</h2>
                              <p>
                                   If you have questions about this Privacy Policy, please contact us at:
                              </p>
                              <p className='mt-2'>
                                   Ripout Rentals&reg;<br />
                                   Phoenix, Arizona<br />
                                   <a href='mailto:sales@ripoutrentals.com' className='text-yellow hover:underline'>
                                        sales@ripoutrentals.com
                                   </a>
                              </p>
                         </section>
                    </div>
               </div>
          </div>
     );
}
